
import React, { useState, useEffect, useMemo } from 'react';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import { HealthAIService } from '../services/geminiService';
import { HealthLog, PredictionResult } from '../types';
import { THAI_LABELS } from '../constants';
import { supabase } from '../services/supabase';
import Swal from 'sweetalert2';

interface SimulatorProps {
  isDarkMode?: boolean;
}

const Simulator: React.FC<SimulatorProps> = ({ isDarkMode }) => {
  const [discipline, setDiscipline] = useState<number>(50);
  const [prediction, setPrediction] = useState<PredictionResult | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [dataLoading, setDataLoading] = useState<boolean>(true);
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
  const [logs, setLogs] = useState<HealthLog[]>([]);
  
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');

  const loadingMessages = [
    "AI กำลังวิเคราะห์ข้อมูลสุขภาพของคุณ...",
    "กำลังประมวลผลแนวโน้มจากประวัติย้อนหลัง...",
    "กำลังคำวณความสัมพันธ์กับระดับวินัย...",
    "เตรียมคำแนะนำเฉพาะบุคคลสำหรับคุณ..."
  ];

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    try {
      setDataLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data, error } = await supabase
          .from('health_logs')
          .select('*')
          .order('date', { ascending: true });
        
        if (error) throw error;
        if (data) setLogs(data);
      }
    } catch (err) {
      console.error("Error fetching logs for simulator:", err);
    } finally {
      setDataLoading(false);
    }
  };

  useEffect(() => {
    let interval: number;
    if (loading) {
      interval = window.setInterval(() => {
        setLoadingMessageIndex((prev) => (prev + 1) % loadingMessages.length);
      }, 2000);
    } else {
      setLoadingMessageIndex(0);
    }
    return () => clearInterval(interval);
  }, [loading]);

  const filteredLogs = useMemo(() => {
    return logs.filter(log => {
      const logTime = new Date(log.date).getTime();
      const start = startDate ? new Date(startDate).getTime() : -Infinity;
      const end = endDate ? new Date(endDate).getTime() : Infinity;
      return logTime >= start && logTime <= end;
    });
  }, [logs, startDate, endDate]);

  const handleSimulate = async () => {
    if (logs.length === 0) {
      Swal.fire({
        title: 'ไม่พบข้อมูลสุขภาพ',
        text: 'กรุณาเพิ่มบันทึกสุขภาพก่อนเริ่มการทำนาย',
        icon: 'warning',
        confirmButtonColor: '#2563eb'
      });
      return;
    }

    setLoading(true);
    setPrediction(null);
    try {
      const service = new HealthAIService();
      const res = await service.predictFutureHealth(logs, discipline);
      setPrediction(res);
    } catch (error) {
      console.error(error);
      Swal.fire({
        title: 'การทำนายล้มเหลว',
        text: 'เกิดข้อผิดพลาดในการทำนาย กรุณาลองใหม่อีกครั้ง',
        icon: 'error',
        confirmButtonColor: '#2563eb'
      });
    } finally {
      setLoading(false);
    }
  };

  const getRiskMetadata = (level: string) => {
    switch (level) {
      case 'สูง':
        return {
          color: 'text-red-600 dark:text-red-400', 
          bg: 'bg-red-50 dark:bg-red-900/20', 
          border: 'border-red-200 dark:border-red-900/30',
          desc: 'ควรพบแพทย์เพื่อวางแผนการรักษาอย่างใกล้ชิด',
          icon: <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
        };
      case 'ปานกลาง':
        return {
          color: 'text-amber-600 dark:text-amber-400', 
          bg: 'bg-amber-50 dark:bg-amber-900/20', 
          border: 'border-amber-200 dark:border-amber-900/30',
          desc: 'ควรปรับเปลี่ยนพฤติกรรมและเฝ้าระวังอาการ',
          icon: <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
        };
      default:
        return {
          color: 'text-green-600 dark:text-green-400', 
          bg: 'bg-green-50 dark:bg-green-900/20', 
          border: 'border-green-200 dark:border-green-900/30',
          desc: 'สุขภาพอยู่ในเกณฑ์ดี รักษามาตรฐานนี้ไว้',
          icon: <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
        };
    }
  };

  if (dataLoading) return (
    <div className="flex flex-col items-center justify-center p-20 space-y-4">
      <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      <p className="text-slate-400 dark:text-slate-500 font-bold text-xs">กำลังโหลดประวัติสุขภาพ...</p>
    </div>
  );

  return (
    <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-800 max-w-2xl mx-auto space-y-8 animate-in fade-in duration-500 transition-colors">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center text-blue-600 dark:text-blue-400">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
        </div>
        <div>
          <h2 className="text-xl font-bold text-slate-800 dark:text-slate-100">จำลองสุขภาพด้วย AI</h2>
          <p className="text-xs text-slate-500 dark:text-slate-400">อ้างอิงจากประวัติการตรวจสุขภาพจริงในระบบของคุณ</p>
        </div>
      </div>
      
      {/* Historical Data Summary Chart */}
      <div className="p-5 bg-slate-50 dark:bg-slate-800 rounded-2xl border border-slate-100 dark:border-slate-700 transition-colors">
        <h3 className="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-4">แนวโน้มย้อนหลังจาก Supabase</h3>
        <div className="h-[200px] w-full">
          {filteredLogs.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={filteredLogs} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={isDarkMode ? "#334155" : "#e2e8f0"} />
                <XAxis 
                  dataKey="date" 
                  stroke={isDarkMode ? "#64748b" : "#94a3b8"} 
                  fontSize={10} 
                  fontWeight="bold"
                  tickLine={false} 
                  axisLine={false} 
                  tickFormatter={(str) => new Date(str).toLocaleDateString('th-TH', { month: 'short' })}
                />
                <YAxis stroke={isDarkMode ? "#64748b" : "#94a3b8"} fontSize={10} fontWeight="bold" tickLine={false} axisLine={false} />
                <Tooltip 
                   contentStyle={{ 
                    borderRadius: '12px', 
                    border: 'none', 
                    backgroundColor: isDarkMode ? '#1e293b' : '#ffffff',
                    color: isDarkMode ? '#f1f5f9' : '#1e293b',
                    fontSize: '12px' 
                  }}
                />
                <Line name="น้ำหนัก" type="monotone" dataKey="weight" stroke="#3b82f6" strokeWidth={2} dot={false} />
                <Line name="ความดัน" type="monotone" dataKey="systolic" stroke="#ef4444" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-full flex items-center justify-center text-slate-400 dark:text-slate-600 text-xs italic">ไม่พบประวัติสุขภาพเพื่อนำมาเปรียบเทียบ</div>
          )}
        </div>
      </div>

      <div className="bg-blue-50/30 dark:bg-blue-900/10 p-6 rounded-2xl border border-blue-100/50 dark:border-blue-900/20">
        <div className="flex justify-between items-end mb-4">
          <div>
            <label className="text-sm font-bold text-slate-700 dark:text-slate-300">{THAI_LABELS.DISCIPLINE}</label>
            <p className="text-xs text-slate-500 dark:text-slate-400">สมมติฐานการดูแลตนเองในอีก 3 เดือนข้างหน้า</p>
          </div>
          <span className="text-3xl font-black text-blue-600 dark:text-blue-400">{discipline}</span>
        </div>
        <input 
          type="range" 
          min="0" 
          max="100" 
          value={discipline}
          onChange={(e) => setDiscipline(parseInt(e.target.value))}
          className="w-full h-2 bg-slate-200 dark:bg-slate-700 rounded-lg appearance-none cursor-pointer accent-blue-600 dark:accent-blue-500"
        />
        <div className="flex justify-between mt-2 text-[10px] font-bold text-slate-400 dark:text-slate-600">
          <span>ละเลย (0)</span>
          <span>ดีเยี่ยม (100)</span>
        </div>
      </div>

      <button 
        onClick={handleSimulate}
        disabled={loading || logs.length === 0}
        className="w-full py-4 bg-blue-600 dark:bg-blue-500 hover:bg-blue-700 dark:hover:bg-blue-600 text-white font-bold rounded-xl transition-all shadow-lg shadow-blue-100 dark:shadow-none disabled:opacity-50 flex items-center justify-center gap-2"
      >
        {loading ? (
          <>
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            <span>{loadingMessages[loadingMessageIndex]}</span>
          </>
        ) : (
          <span>{THAI_LABELS.PREDICT}</span>
        )}
      </button>

      {prediction && (
        <div className="mt-8 space-y-6 animate-in fade-in slide-in-from-bottom-4">
          <div className={`p-6 rounded-2xl border-2 shadow-xl ${getRiskMetadata(prediction.riskLevel).bg} ${getRiskMetadata(prediction.riskLevel).border} transition-colors`}>
            <div className="flex items-center gap-4 mb-3">
              <div className={`${getRiskMetadata(prediction.riskLevel).color}`}>
                {getRiskMetadata(prediction.riskLevel).icon}
              </div>
              <div>
                <p className={`text-2xl font-black ${getRiskMetadata(prediction.riskLevel).color}`}>
                  เสี่ยงระดับ: {prediction.riskLevel}
                </p>
                <p className="text-xs text-slate-600 dark:text-slate-400 font-bold">{getRiskMetadata(prediction.riskLevel).desc}</p>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4 mt-6">
              <div className="bg-white/80 dark:bg-slate-800/80 p-3 rounded-xl">
                <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase">น้ำหนักคาดการณ์</p>
                <p className="text-lg font-black text-slate-700 dark:text-slate-200">{prediction.futureWeight.toFixed(1)} กก.</p>
              </div>
              <div className="bg-white/80 dark:bg-slate-800/80 p-3 rounded-xl">
                <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase">ความดันคาดการณ์</p>
                <p className="text-lg font-black text-slate-700 dark:text-slate-200">{prediction.futureSystolic}/{prediction.futureDiastolic}</p>
              </div>
            </div>
          </div>

          <div className="p-6 bg-white dark:bg-slate-800 rounded-2xl border-2 border-slate-100 dark:border-slate-700 shadow-sm transition-colors">
            <h3 className="font-bold text-slate-800 dark:text-slate-100 mb-4 flex items-center gap-2">
              <svg className="w-5 h-5 text-blue-500 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              คำแนะนำจาก AI
            </h3>
            <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed font-medium">"{prediction.aiAdvice}"</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Simulator;
